/**
 * LIFF URL Utilities
 * Helper functions for generating LIFF URLs for different view types
 */

interface LiffUrlOptions {
  productId?: string;
  viewType?: 'compact' | 'tall' | 'full';
  path?: string;
}

/**
 * Get the base LIFF URL
 * @returns Base LIFF URL (https://liff.line.me/{liffId})
 */
export const getLiffBaseUrl = (): string => {
  const liffId = import.meta.env.VITE_LINE_LIFF_ID || '2009616421-TcSkTA1q';
  return `https://liff.line.me/${liffId}`;
};

/**
 * Generate a LIFF URL for a product page
 * @param options - Options for generating the URL
 * @returns Complete LIFF URL
 *
 * @example
 * // Generate compact view URL for product
 * generateLiffProductUrl('prod-123', 'compact')
 * // => https://liff.line.me/2009616421-TcSkTA1q?path=/good/prod-123&view=compact
 *
 * @example
 * // Generate full view URL for product
 * generateLiffProductUrl('prod-123', 'full')
 * // => https://liff.line.me/2009616421-TcSkTA1q?path=/good/prod-123&view=full
 */
export const generateLiffProductUrl = (
  productId: string,
  viewType: 'compact' | 'tall' | 'full' = 'compact'
): string => {
  const baseUrl = getLiffBaseUrl();
  const params = new URLSearchParams({
    path: `/good/${productId}`,
    view: viewType,
  });
  return `${baseUrl}?${params.toString()}`;
};

/**
 * Generate a LIFF URL for any path
 * @param options - Options for generating the URL
 * @returns Complete LIFF URL
 *
 * @example
 * // Generate custom path URL
 * generateLiffUrl({ path: '/cart', viewType: 'tall' })
 * // => https://liff.line.me/2009616421-TcSkTA1q?path=/cart&view=tall
 */
export const generateLiffUrl = (options: LiffUrlOptions): string => {
  const baseUrl = getLiffBaseUrl();
  const params = new URLSearchParams();

  if (options.productId) {
    params.set('path', `/good/${options.productId}`);
  } else if (options.path) {
    params.set('path', options.path);
  }

  if (options.viewType) {
    params.set('view', options.viewType);
  }

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Copy text to clipboard
 * @param text - Text to copy
 * @returns Promise that resolves when text is copied
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        textArea.remove();
        return true;
      } catch (err) {
        textArea.remove();
        return false;
      }
    }
  } catch (err) {
    console.error('[copyToClipboard] Failed to copy:', err);
    return false;
  }
};

/**
 * Share LIFF URL via Web Share API if available
 * @param title - Title to share
 * @param text - Text to share
 * @param url - URL to share
 * @returns Promise that resolves when shared
 */
export const shareLiffUrl = async (
  title: string,
  text: string,
  url: string
): Promise<boolean> => {
  try {
    if (navigator.share) {
      await navigator.share({
        title,
        text,
        url,
      });
      return true;
    }
    return false;
  } catch (err) {
    console.error('[shareLiffUrl] Share failed:', err);
    return false;
  }
};

/**
 * LIFF View Type descriptions for UI display
 */
export const LIFF_VIEW_TYPE_INFO = {
  compact: {
    label: '精簡視圖',
    description: '佔螢幕 50%，適合快速瀏覽',
    icon: '📱',
  },
  tall: {
    label: '加高視圖',
    description: '佔螢幕 80%，適合查看細節',
    icon: '📱',
  },
  full: {
    label: '全螢幕視圖',
    description: '佔滿整個螢幕，適合完整體驗',
    icon: '🖥️',
  },
} as const;
